/**
 * 本文と用語辞書から「リンク化する区間」を切り出す（大枠設計 §7 の表示パターン #4「用語解決」）。
 *
 * 当てる相手は **detail 応答が同梱する解決ではなく、画面が 1 回だけ取る辞書**である。解決を
 * 応答ごとに同梱する形は、本文を持つ応答すべてに位置つきの解決を積む必要があり、実際には
 * 総合テストの説明文 1 箇所にしか積まれていなかった。一覧の行やタイトルには積みようが無い
 * （行ごとに用語解決を走らせることになる）。辞書を 1 つ持てば、本文がどこにあっても同じ
 * 規則で当てられる。
 *
 * 一致の判定そのものは共通部の `termOccurrencesIn` に委ねる。同じ判定が USES_TERM エッジの
 * 抽出にも使われており、ここに書き写すと抽出とリンクが別の規則で動く。そうなると「使用箇所
 * には数えられているのに本文ではリンクされない」語が生まれ、見た目は正常なので気付けない。
 *
 * 重なり合う出現は先勝ちで捨てる。「アイテム」と「アイテム一覧」の両方が同じ位置から始まる
 * とき、両方をリンクにすると本文が二重に描かれる。長い方を優先するために、同じ開始位置では
 * 長い出現を先に並べてから先勝ちさせる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 本文と用語辞書から、リンク化する区間と素のテキストの並びを組み立てる。
 * @graph-connects termOccurrencesIn [calls] 出現位置の判定を共通部と同一経路にする
 */
import { termOccurrencesIn } from '@cortex/domain-spec-console';

/**
 * 本文に当てる辞書 1 件。
 *
 * **契約の型ではなく、この module が要る欄だけを名指しする。** 辞書の出どころ（今は
 * `listTerms`）が替わっても、当て方は変わらない。契約の型を直に受けると、出どころを
 * 替えるたびに切り分けの側まで書き換えることになる。
 */
export interface TermLinkEntry {
  /** 用語 ID。リンク先のパスパラメータになる。 */
  termId: string;
  /** 用語の表示名。 */
  label: string;
  /** 用語の定義。hover 表示に使う。引けなければ空文字。 */
  definition: string;
  /** 完全一致で拾う表記の索引。 */
  surfaces: {
    /** 正表記。 */
    canonical: string;
    /** 完全一致で拾う表記すべて。 */
    all: readonly string[];
    /** 揺れ表記だけ。 */
    synonyms: readonly string[];
  };
}

/** リンク先の用語。`TermTextSegment` が指す先の最小情報。 */
export interface SegmentTerm {
  /** 用語 ID。 */
  termId: string;
  /** 用語の日本語表記。 */
  label: string;
  /** 用語の定義。hover 表示に使う。 */
  definition: string;
}

/** 本文を切り分けた 1 区間。`term` が null なら素のテキストである。 */
export interface TermTextSegment {
  /** React key。 */
  key: string;
  /** 区間の文字列。 */
  text: string;
  /**
   * この区間が本文の何文字目から始まるか。
   *
   * 差分の強調（`HighlightedText`）は本文全体の座標で区間を持つので、切り分けた後の 1 片にも
   * 同じ区間を当てるにはこの位置が要る。切り分けの側が既に知っている値なので、描画側で
   * 数え直させない — 数え直すと、切り方を変えたときに強調だけが横へずれる。
   */
  offset: number;
  /** リンク先の用語。素のテキストなら null。 */
  term: SegmentTerm | null;
}

/** 位置合わせの途中で扱う出現 1 件。 @graph-connects none */
interface TermMatch {
  offset: number;
  length: number;
  term: SegmentTerm;
}

/**
 * 辞書 1 件を共通部の表記索引の形へ写す。
 *
 * `nodeId` は判定に使われないが、共通部の索引が必須で持つ欄なので用語 ID を入れる。空文字を
 * 入れると、索引を受け取った側が「持ち主の分からない表記集合」を扱えてしまう。
 * @graph-connects none
 */
function toSurfaces(entry: TermLinkEntry) {
  return {
    nodeId: entry.termId,
    canonical: entry.surfaces.canonical,
    all: entry.surfaces.all,
    synonyms: entry.surfaces.synonyms,
  };
}

/**
 * 本文に当たった出現を、開始位置順（同じ位置なら長い順）に並べて取り出す。
 *
 * @param text 当てる本文
 * @param terms 用語辞書
 * @param suppressedTermId この用語へのリンクは作らない（自分自身のページで使う）
 * @returns 整列した出現
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語辞書を本文へ当て、リンク候補の出現を整列して取り出す。
 * @graph-connects termOccurrencesIn [calls] 出現位置の判定
 */
function collectMatches(
  text: string,
  terms: readonly TermLinkEntry[],
  suppressedTermId: string | null,
): TermMatch[] {
  const matches: TermMatch[] = [];

  for (const entry of terms) {
    // 自分自身の定義文の中で自分へのリンクを作らない。押しても同じ場所へ戻るだけの導線が
    // 定義の本文に何度も挟まり、読み物として追えなくなる。
    if (entry.termId === suppressedTermId) continue;

    for (const occurrence of termOccurrencesIn(text, toSurfaces(entry))) {
      matches.push({
        offset: occurrence.start,
        length: occurrence.end - occurrence.start,
        term: {
          termId: entry.termId,
          label: entry.label,
          definition: entry.definition,
        },
      });
    }
  }

  return matches.sort((left, right) =>
    left.offset === right.offset ? right.length - left.length : left.offset - right.offset,
  );
}

/**
 * 本文を「素のテキスト」と「用語リンク」の区間へ切り分ける。
 *
 * 本文が空文字なら区間を 1 つも返さない。空の区間を返すと、呼び出し側が空の `<span>` を
 * 描いてしまい、未記入と「用語が無いだけ」の区別が付かなくなる。
 *
 * 辞書が空でも同じ形（素のテキスト 1 区間）を返す。辞書はまだ届いていないだけということが
 * あり、そのときに本文ごと描かないと、リンクの有無ではなく**本文の有無**が揺れる。
 *
 * @param text 当てる本文
 * @param terms 用語辞書
 * @param suppressedTermId この用語へのリンクは作らない（自分自身のページで使う）
 * @returns 切り分けた区間の並び
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 本文と用語辞書から、リンク化する区間と素のテキストの並びを組み立てる。
 * @graph-connects collectMatches [calls] リンク候補の取り出し
 */
export function toTermSegments(
  text: string,
  terms: readonly TermLinkEntry[],
  suppressedTermId: string | null = null,
): TermTextSegment[] {
  if (text === '') return [];

  const segments: TermTextSegment[] = [];
  let cursor = 0;

  for (const match of collectMatches(text, terms, suppressedTermId)) {
    if (match.offset < cursor) continue;

    if (match.offset > cursor) {
      segments.push({
        key: `plain:${String(cursor)}`,
        text: text.slice(cursor, match.offset),
        offset: cursor,
        term: null,
      });
    }

    segments.push({
      key: `term:${match.term.termId}:${String(match.offset)}`,
      text: text.slice(match.offset, match.offset + match.length),
      offset: match.offset,
      term: match.term,
    });
    cursor = match.offset + match.length;
  }

  if (cursor < text.length) {
    segments.push({
      key: `plain:${String(cursor)}`,
      text: text.slice(cursor),
      offset: cursor,
      term: null,
    });
  }

  return segments;
}
